/**
 * Absa Life "call me back" handoff URL builder.
 *
 * Target form: https://www.absa.co.za/personal/insure/my-funeral/call-me-back/
 * Confirmed fields (scraped from the live form, salesforce-form):
 *   first_name  (required)
 *   last_name   (required)
 *   mobile      (required, ZA number)
 *   email       (required)
 *   + captcha the user must complete
 *
 * The form is an Adobe AEM form. AEM does NOT guarantee URL-param prefill,
 * so treat this as best-effort: if the params aren't picked up, the user
 * just sees an empty form — nothing breaks.
 *
 * Track metadata (artist, song title, dedicate-to) is NEVER placed in
 * applicant fields — those refer to different people. Track context is
 * only ever passed as UTM / attribution params so Absa's consultant can
 * see the campaign origin.
 */

const ABSA_QUOTE_URL = "https://www.absa.co.za/personal/insure/my-funeral/call-me-back/";

// Prefill is now enabled — we have Absa's confirmed field names.
const ABSA_PREFILL_ENABLED = true;

// Exact AEM form field names as scraped from the live Absa call-me-back page.
const PREFILL_KEYS = {
  firstName: "first_name",
  lastName: "last_name",
  mobile: "mobile",
  email: "email",
} as const;

const CAMPAIGN_ID = "love_language_fm_2026";

export type AbsaHandoffUser = {
  fullName?: string;
  mobile?: string;
  email?: string;
};

export type AbsaHandoffTrack = {
  id?: string | number;
  title?: string;
  artist?: string;
};

export type AbsaHandoffArgs = {
  source: "track_card" | "dedication_optin";
  track?: AbsaHandoffTrack;
  user?: AbsaHandoffUser;
};

function shortRef(): string {
  return (
    Date.now().toString(36) + Math.random().toString(36).slice(2, 8)
  ).toUpperCase();
}

function splitName(full?: string): { firstName: string; lastName: string } {
  const s = (full ?? "").trim().replace(/\s+/g, " ");
  if (!s) return { firstName: "", lastName: "" };
  const idx = s.lastIndexOf(" ");
  if (idx === -1) return { firstName: s, lastName: "" };
  return { firstName: s.slice(0, idx), lastName: s.slice(idx + 1) };
}

// Normalise ZA mobile: strip spaces, dashes, +27 → 0-prefix, keep digits only.
function normaliseZaMobile(raw?: string): string {
  if (!raw) return "";
  let s = raw.replace(/[^\d+]/g, "");
  if (s.startsWith("+27")) s = "0" + s.slice(3);
  else if (s.startsWith("27") && s.length === 11) s = "0" + s.slice(2);
  s = s.replace(/\D/g, "");
  // Absa's form validates as ^\d{1,10}$; trim to 10 if longer.
  return s.slice(0, 10);
}

export function buildAbsaQuoteUrl(args: AbsaHandoffArgs): {
  url: string;
  ref: string;
} {
  const ref = shortRef();
  const params = new URLSearchParams();
  const trackId = args.track?.id != null ? String(args.track.id) : undefined;

  // Attribution — always safe, no PII.
  params.set("utm_source", "lovelanguagefm");
  params.set("utm_medium", "referral");
  params.set("utm_campaign", CAMPAIGN_ID);
  params.set(
    "utm_content",
    args.source === "track_card" && trackId ? `track_${trackId}` : args.source,
  );
  params.set("llfm_campaign_id", CAMPAIGN_ID);
  params.set("llfm_ref", ref);
  if (trackId) params.set("llfm_track_ref", trackId);
  // Track metadata as CONTEXT ONLY — never in applicant fields.
  if (args.track?.title) params.set("llfm_track_title", args.track.title.slice(0, 120));
  if (args.track?.artist) params.set("llfm_track_artist", args.track.artist.slice(0, 120));

  // Prefill — only for dedication opt-in path where we have the user's own
  // PII under POPIA consent captured on the same page.
  if (ABSA_PREFILL_ENABLED && args.source === "dedication_optin" && args.user) {
    const { firstName, lastName } = splitName(args.user.fullName);
    if (firstName) params.set(PREFILL_KEYS.firstName, firstName);
    if (lastName) params.set(PREFILL_KEYS.lastName, lastName);
    const mobile = normaliseZaMobile(args.user.mobile);
    if (mobile) params.set(PREFILL_KEYS.mobile, mobile);
    if (args.user.email) params.set(PREFILL_KEYS.email, args.user.email.trim());
  }

  return { url: `${ABSA_QUOTE_URL}?${params.toString()}`, ref };
}
